import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Card, Stack, Box, Typography, MenuItem, IconButton, Grid, Avatar, Skeleton, Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import axios, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';
import { FormProvider, RHFTextField, RHFSelect } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import useIsMountedRef from 'hooks/useIsMountedRef';

// ====== Validation Schema ======
const NewUserSchema = Yup.object().shape({
  start_amount: Yup.number("Enter a valid amount")
    .required('Required')
    .min(0, "Start amount cannot be less than zero"),
  end_amount: Yup.number("Enter a valid amount")
    .required('Required')
    .moreThan(Yup.ref('start_amount'), "To amount must be greater than From amount"),
  spread_amount: Yup.number("Enter a valid amount")
    .required('Required')
    .positive("Amount must be greater than zero"),
  rate: Yup.number()
    .typeError("Enter a valid rate")
    .required('Required')
    .positive("Rate must be greater than zero"),
  coin_id: Yup.string().required('Choose a crypto'),
  transaction_type: Yup.string().required('Choose a type')
});

SetCryptoRateModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleComplete: PropTypes.func,
  isEdit: PropTypes.bool,
  endpointURL: PropTypes.string,
  range: PropTypes.object, 
};

export default function SetCryptoRateModal({ open, handleClose, handleComplete, isEdit, range, endpointURL }) {
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const [state, setState] = useState({});
  const [cryptoList, setCryptoList] = useState([]);

  // Fetch crypto asset list
  const getInitData = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, loadComplete: false, error: null }));
    try {
      const response = await axios.get(CRUD.cryptoAssets);
      if (isMountedRef.current) setCryptoList(response.data);
      setState((s) => ({ ...s, loading: false, loadComplete: true, error: null }));
    } catch (error) {
      setState((s) => ({ ...s, loading: false, loadComplete: false, error: getErrorMessage(error) }));
    }
  }, [isMountedRef]);

  useEffect(() => {
    getInitData();
  }, [getInitData]);

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    mode: 'onChange',
    defaultValues: {
      start_amount: range?.start_amount || '',
      end_amount: range?.end_amount || '',
      spread_amount: range?.spread_amount || '',
      coin_id: range?.coin_id || '',
      rate: range?.rate || '',
      transaction_type: range?.transaction_type || '',
    },
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isSubmitting, errors, isValid }
  } = methods;

  const values = watch();
  const selectedCoin = isEdit
    ? { ...range }
    : cryptoList.find((item) => item.coin_id === values.coin_id);

  // -- Fix for dropdown not showing: Use RHFSelect directly and pass MenuItems as children;
  // Don't wrap RHFSelect with Controller unless necessary
  // (Assuming your RHFSelect is implemented to use react-hook-form's useController internally.)

  const onSubmit = async (form) => {
    try {
      if (isEdit) {
        form.coin_id = range.coin_id;
        form.id = range.id;
        await axios.post(`${endpointURL}/update`, form);
      } else {
        await axios.post(`${endpointURL}`, form);
      }
      reset();
      enqueueSnackbar(isEdit ? 'Updated!' : 'Created!', { variant: 'success' });
      handleComplete?.();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  };

  // Preview card data structure for Auditor
  const previewData = {
    Asset: selectedCoin ? `${selectedCoin.name} (${selectedCoin.coin_id})` : '-',
    'From (USD)': values.start_amount || '-',
    'To (USD)': values.end_amount || '-',
    'Spread (NGN)': values.spread_amount || '-',
    'Rate (NGN)': values.rate || '-',
    'Transaction type': values.transaction_type || '-',
  };

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: 8, px: 0 },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          pt: 3,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {selectedCoin?.image_url && (
            <Avatar src={selectedCoin.image_url} sx={{ width: 42, height: 42, bgcolor: "grey.100" }} />
          )}
          <Typography variant="h5" fontWeight={700}>
            {isEdit ? "Update Crypto Rate Range" : "Add Crypto Rate Range"}
          </Typography>
          {selectedCoin?.name && (
            <Typography variant="subtitle1" color="text.secondary" ml={2} fontWeight="bold">
              {selectedCoin.name} ({selectedCoin.coin_id})
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="large" sx={{ color: "grey.500" }} aria-label="close">
          <Icon icon="eva:close-fill" width={24} height={24} />
        </IconButton>
      </DialogTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ px: 4, pt: 1, pb: 0 }}>
            <Grid container spacing={3}>
              {/* Coin select dropdown: Use RHFSelect directly, no extra Controller */}
              {!isEdit && (
                <Grid item xs={12}>
                  {state.loading ? (
                    <Skeleton variant="rectangular" height={56} />
                  ) : (
                    <RHFSelect
                      name="coin_id"
                      label="Cryptocurrency"
                      required
                      error={!!errors.coin_id}
                      helperText={errors.coin_id?.message}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value=""><em>Select an asset...</em></MenuItem>
                      {cryptoList.map((option) => (
                        <option key={option.coin_id} value={option.coin_id}>
                            {option.name} ({option.coin_id})
                        </option>
                        ))}
                      {/* ))} */}
                    </RHFSelect>
                  )}
                </Grid>
              )}
              {/* {isEdit && selectedCoin && (
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      backgroundColor: "grey.100",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Avatar src={selectedCoin.image_url} alt={selectedCoin.name} sx={{ width: 52, height: 52 }} />
                    <Box>
                      <Typography fontWeight={700}>
                        {selectedCoin.name} ({selectedCoin.coin_id})
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        {selectedCoin.symbol?.toUpperCase()}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              )} */}

              <Grid item xs={6}>
                <RHFTextField
                  name="start_amount"
                  label="From amount (USD)"
                  type="number"
                  required
                  InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ fontWeight: 700, pr: 0.5 }}>$</Typography> }}
                />
              </Grid>
              <Grid item xs={6}>
                <RHFTextField
                  name="end_amount"
                  label="To amount (USD)"
                  type="number"
                  required
                  InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ fontWeight: 700, pr: 0.5 }}>$</Typography> }}
                />
              </Grid>
              <Grid item xs={6}>
                <RHFTextField
                  name="rate"
                  label="Rate (NGN)"
                  type="number"
                  required
                  InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ fontWeight: 700, pr: 0.5 }}>₦</Typography> }}
                />
              </Grid>
              <Grid item xs={6}>
                <RHFTextField
                  name="spread_amount"
                  label="Spread (NGN)"
                  type="number"
                  required
                  InputProps={{ startAdornment: <Typography color="text.secondary" sx={{ fontWeight: 700, pr: 0.5 }}>₦</Typography> }}
                />
              </Grid>
              <Grid item xs={12}>
                {/* <RHFSelect
                  name="transaction_type"
                  label="Transaction type"
                  required
                  error={!!errors.transaction_type}
                  helperText={errors.transaction_type?.message}
                >
                  <MenuItem value=""><em>Select type...</em></MenuItem>
                  <MenuItem value="buy">Buy</MenuItem>
                  <MenuItem value="sell">Sell</MenuItem>
                </RHFSelect> */}
                <RHFSelect name="transaction_type" label="Transaction type" sx={{mt:3}} placeholder="Select option" c >
                    <option value="" />
                    {['sell','buy'].map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                    ))}
                </RHFSelect>
              </Grid>
            </Grid>
            <Divider sx={{ my: 4 }} />

            {/* Data preview card for auditor confirmation */}
            <Card
              variant="outlined"
              sx={{
                background: "linear-gradient(90deg, #f8fafc 60%, #F4F8FE 100%)",
                border: "1px solid #e4eaf0",
                p: 3,
              }}
            >
              <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                Preview: Data To Be Submitted
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(previewData).map(([label, value]) => (
                  <Grid item xs={6} key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3 }}>
            <Button onClick={handleClose} variant="outlined" size="large">
              Cancel
            </Button>
            <LoadingButton
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={!isValid || isSubmitting}
            >
              {isEdit ? "Save changes" : "Save range"}
            </LoadingButton>
          </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
